// Pure helpers for text-based lead extraction (merge, strip locks, field extractors).
import type { ExtractedLeadV2, LockedFields } from "../../../types/lead";
import { formatPhoneStrict } from "@/lib/lead/phones";

/** Keep newer only if it's meaningfully filled; otherwise fall back to older. */
export const preferNonEmpty = <T,>(newer: T, older: T): T => {
  if (newer === null || newer === undefined) return older;
  if (typeof newer === "string" && newer.trim() === "") return older;
  if (Array.isArray(newer) && newer.length === 0) return older;
  return newer;
};

export const isValidEmail = (email: string | null | undefined): boolean => {
  const e = (email || "").trim().toLowerCase();
  if (!e) return false;
  if (e.split("@").length !== 2) return false;
  const [local, domain] = e.split("@");
  if (!local || !domain) return false;
  if (!domain.includes(".")) return false;
  return true;
};

export const normalizeName = (s: string | null | undefined) => (s || "").trim().toLowerCase();
export const normalizeEmail = (s: string | null | undefined) => (s || "").trim().toLowerCase();
export const normalizePhone = (s: string | null | undefined) => (s || "").replace(/\D/g, "");

const spokenDigitToChar = (w: string): string | null => {
  const s = w.toLowerCase().replace(/[^a-z]/g, "");
  const map: Record<string, string> = {
    zero: "0",
    oh: "0",
    one: "1",
    two: "2",
    three: "3",
    four: "4",
    five: "5",
    six: "6",
    seven: "7",
    eight: "8",
    nine: "9",
  };
  return map[s] ?? null;
};

const normalizeSpokenDigits = (text: string): string => {
  return (text || "").replace(/\b(zero|oh|one|two|three|four|five|six|seven|eight|nine)\b/gi, (m) => spokenDigitToChar(m) ?? m);
};

export const extractPhoneCandidateFromText = (text: string): string | null => {
  const raw = text || "";
  if (!raw.trim()) return null;

  const t = normalizeSpokenDigits(raw);

  const regionMatch = t.match(/\b(?:phone(?: number)? is|my number is|call me at)\b([\s\S]{0,80})/i);
  const region = regionMatch?.[1] ? regionMatch[1] : t;

  let digits = (region || "").replace(/\D/g, "");

  if (digits.length >= 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length > 10) digits = digits.slice(-10);
  if (digits.length !== 10) return null;

  return formatPhoneStrict(digits);
};

const sanitizeEmailCandidate = (email: string): string | null => {
  if (!email) return null;

  let e = email.trim().toLowerCase();
  e = e.replace(/[)\],.!?;:]+$/g, "");
  e = e.replace(/\.@/g, "@");
  e = e.replace(/\.{2,}/g, ".");

  if (e.endsWith(".my")) {
    const without = e.slice(0, -3);
    if (isValidEmail(without)) e = without;
  }

  return isValidEmail(e) ? e : null;
};

export const extractEmailFromText = (text: string): string | null => {
  const raw = text || "";
  if (!raw.trim()) return null;

  const direct = raw.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  if (direct?.[0]) return sanitizeEmailCandidate(direct[0]);

  let t = raw.toLowerCase();
  t = t
    .replace(/\s+at\s+/g, "@")
    .replace(/\s+dot\s+/g, ".")
    .replace(/\s+underscore\s+/g, "_")
    .replace(/\s+dash\s+/g, "-")
    .replace(/\s+plus\s+/g, "+");

  t = t.replace(/\s*@\s*/g, "@").replace(/\s*\.\s*/g, ".");
  const after = t.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  if (after?.[0]) return sanitizeEmailCandidate(after[0]);

  return null;
};

export const extractFullNameFromText = (text: string): { first: string; last: string } | null => {
  const t = (text || "").trim();
  if (!t) return null;

  const explicit = t.match(/\b(?:my name is|this is|i am|i'm)\s+([A-Za-z]+)\s+([A-Za-z'-]+)\b/i);
  if (explicit) return { first: explicit[1]!, last: explicit[2]! };

  // ASR often drops "my name" → "Is Jerome Jackson" at utterance start.
  const asrLead = t.match(/^(?:is|it's|its|this is)\s+([A-Za-z][A-Za-z'-]*)\s+([A-Za-z][A-Za-z'-]+)\b/i);
  if (asrLead) return { first: asrLead[1]!, last: asrLead[2]! };

  const nameIs = t.match(/\bname is\s+([A-Za-z]+)\s+([A-Za-z'-]+)\b/i);
  if (nameIs) return { first: nameIs[1]!, last: nameIs[2]! };

  return null;
};

/** Model/heuristic misparses of "I'm interested in …" as a customer name. */
export function isLikelyInterestPhraseName(
  first: string | null | undefined,
  last: string | null | undefined,
): boolean {
  const f = (first || "").trim().toLowerCase();
  const l = (last || "").trim().toLowerCase();
  if (!f) return false;
  if (f === "interested" || f === "i'm" || f === "im") return true;
  if (f === "interested" && (l === "in" || !l)) return true;
  if (f.startsWith("interested")) return true;
  return false;
};

export function extractLeadSourceFromText(
  text: string,
): { category: "facebook" | "google" | "word_of_mouth" | "drive_by" | "referral"; detail: string | null } | null {
  const t = (text || "").toLowerCase();
  if (!t.trim()) return null;

  if (/\bfacebook\b|\bfb\s+ad\b|\bfacebook\s+ad\b|\bmeta\s+ad\b/.test(t)) {
    const heard = text.match(/\b(?:heard|saw|found)\b[^.?!]{0,80}\bfacebook\b[^.?!]*/i);
    return {
      category: "facebook",
      detail: heard?.[0]?.trim().slice(0, 160) ?? "Facebook ad",
    };
  }
  if (/\bgoogle\b|\bgoogle\s+ad\b/.test(t)) {
    return { category: "google", detail: null };
  }
  if (/\bdrive[\s-]?by\b|\bdrove\s+by\b/.test(t)) {
    return { category: "drive_by", detail: null };
  }
  if (/\breferr(?:al|ed)\b|\bfriend\s+told\b|\bneighbor\b/.test(t)) {
    return { category: "referral", detail: null };
  }
  if (/\bword\s+of\s+mouth\b/.test(t)) {
    return { category: "word_of_mouth", detail: null };
  }
  return null;
};

export const extractSizeOptionsFromText = (text: string): string[] => {
  const t = (text || "").toLowerCase();
  if (!t) return [];
  const re = /\b(\d{1,2})\s*(?:x|by)\s*(\d{1,2})\b/g;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(t))) out.push(`${m[1]}x${m[2]}`);
  return Array.from(new Set(out));
};

export const extractProductSubtypeFromText = (text: string): string | null => {
  const t = (text || "").toLowerCase();
  if (t.includes("garage")) return "garage";
  if (t.includes("barn")) return "barn";
  if (t.includes("utility")) return "utility";
  if (t.includes("loft")) return "lofted";
  return null;
};

const isMeaningful = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return true;
  if (typeof value === "object") return Object.values(value).some(isMeaningful);
  return true;
};

export const stripLockedFields = (incoming: ExtractedLeadV2, locked?: LockedFields): Partial<ExtractedLeadV2> => {
  if (!locked) return incoming;
  const out: any = { ...incoming };
  for (const k of Object.keys(locked) as (keyof ExtractedLeadV2)[]) {
    if (locked[k]) delete out[k];
  }
  return out as Partial<ExtractedLeadV2>;
};

export const mergePreferExistingNonBlank = <T extends Record<string, any>>(base: T, incoming: Partial<T>): T => {
  const out: any = { ...(base || {}) };
  for (const [k, v] of Object.entries(incoming || {})) {
    if (!isMeaningful(v)) continue;

    const existing = out[k];

    if (Array.isArray(existing) && Array.isArray(v)) {
      out[k] = Array.from(new Set([...existing, ...v]));
      continue;
    }

    if (
      existing &&
      typeof existing === "object" &&
      !Array.isArray(existing) &&
      v &&
      typeof v === "object" &&
      !Array.isArray(v)
    ) {
      out[k] = mergePreferExistingNonBlank(existing, v as any);
      continue;
    }

    if (isMeaningful(existing)) continue;
    out[k] = v;
  }
  return out as T;
};
